<!--
  资源浏览器组件
  职责：浏览虚拟资源树（URL），支持拖拽到事件编辑器，也支持把本地文件拖入后上传并写入虚拟目录
  数据来源：workspace store 的 virtualResourceRoots
-->
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, h } from 'vue'
import {
  NTabs,
  NTabPane,
  NVirtualList,
  NDropdown,
  useDialog,
} from 'naive-ui'
import type { TreeOption, DropdownOption } from 'naive-ui'
import { useAuthStore } from '@/features/auth'
import { playHtmlAudio, stopHtmlAudio, inferAudioMimeFromName } from '@/features/audio-playback/safariAudioPlayback'
import { useWorkspaceStore } from '@/store/workspace'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useLayoutViewport } from '@/platform/viewport/useLayoutViewport'
import {
  AVG_CANVAS_RESOURCE_MIME,
  AVG_RESOURCE_PATH_MIME,
  RESOURCE_CATEGORIES,
  type ResourceCategoryKey,
  VISIBLE_RESOURCE_CATEGORIES,
  VIRTUAL_FILE_MOVE_MIME,
} from '@/features/virtual-resource/constants'
import {
  buildDefaultVirtualResourceRoot,
  canCreateSubfolderInPath,
  getTopFolderRuleByPath,
  isHiddenResourceTopFolder,
  isLockedTopLevelFolderPath,
  resolveVirtualResourcePathMeta,
  syncVirtualResourcePathMetadata,
} from '@/features/virtual-resource/rules'
import DataFileMap from '@/model/Resource/DataFileMap'
import type MainProject from '@/model/MainProject'
import {
  applyVirtualFolderDrop,
  removeNodeFromTree,
  uniqueFolderName,
  findParent,
} from '@/features/virtual-resource/treeOps'
import {
  applyLocalFontResource,
  applyLocalOuiSkinResource,
  applyLocalVirtualResources,
  canApplyLocalResource,
  type LocalResourceApplyProgress,
} from '@/features/resource-browser/localApply'
import { useProjectLoadingStore } from '@/store/projectLoading'
import { hasSkinResourceMarker } from '@/features/skin-loader/runtime'
import {
  isBitmapFontVirtualUrl,
  resolveBitmapFontPreviewUrl,
} from '@/features/bitmap-font/runtime'
import {
  DEFAULT_PROJECT_FONT_DISPLAY_NAME,
  fetchProjectFontBlobFromUrl,
  isDefaultProjectFontName,
  resolveFontDisplayNameFromAsset,
  resolveFontDisplayNameFromStoredPath,
} from '@/features/project-font/runtime'
import { FONT_DISPLAY_NAME_LINK_INDEX, FONT_PREVIEW_MD5_LINK_INDEX } from '@/features/project-font/library'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'
import { isOafResource } from '@/features/oaf-preview/runtime'
import AppTreeView from '@/components/AppTreeView.vue'
import TextInputDialog from '@/components/TextInputDialog.vue'

const workspace = useWorkspaceStore()
const editor = useSceneEditorStore()
const auth = useAuthStore()
const projectLoading = useProjectLoadingStore()
const dialog = useDialog()
let previewAudio: HTMLAudioElement | null = null

type VirtualFileMovePayload = {
  categoryKey: ResourceCategoryKey
  folderPath: string
  childIndex: number
}

type ResourceClipboard =
  | {
    kind: 'folder'
    categoryKey: ResourceCategoryKey
    path: string
    node: DataFileMap
  }
  | {
    kind: 'file'
    categoryKey: ResourceCategoryKey
    folderPath: string
    childIndex: number
    node: DataFileMap
  }

/* ——— 类型定义 ——— */

type ResourceFolderItem =
  { name: string; path: string; source: 'virtual'; mapNode: DataFileMap }

/** 左侧目录树节点（与 n-tree 的 key / label / children 一致） */
type ResourceFolderTreeNode = TreeOption & {
  key: string
  label: string
  children?: ResourceFolderTreeNode[]
} & (
  { source: 'virtual'; mapNode: DataFileMap }
)

type ResourceFileItem = {
  name: string
  displayName: string
  path: string
  resourceUrl: string
  relativePath: string
  mappingPath: string
  size: number
  ext: string
  thumbUrl: string | null
  previewUrl?: string | null
  /** 虚拟资源：拖入事件属性时使用相对路径。 */
  dragPayload?: string
  /** 在所属虚拟文件夹 mapNode.children 中的下标 */
  virtualChildIndex?: number
  /** 是否为 OUI/OUIQ 皮肤资源。 */
  isSkinResource: boolean
}

type ResourceGridCard =
  | { key: string; kind: 'add' }
  | { key: string; kind: 'file'; file: ResourceFileItem }

type ResourceFileVirtualRow = { key: string; cards: ResourceGridCard[] }

/* ——— 常量 ——— */

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'])
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v'])
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'])
const FONT_FILE_EXTENSIONS = new Set(['ttf', 'otf', 'woff', 'woff2'])
const DESKTOP_CARD_WIDTH = 96
const TOUCH_CARD_WIDTH = 104

/* ——— 状态 ——— */

const selectedResourceCategory = ref<ResourceCategoryKey>('graphic')
/** 当前分类下目录树根（内部保留分类根，左侧树只渲染它的 children） */
const resourceFolderTreeRoot = ref<ResourceFolderTreeNode | null>(null)
/** 展开的目录树节点 key（path） */
const expandedFolderKeys = ref<Array<string | number>>([])
const selectedResourceFolderPath = ref('')
const resourceFiles = ref<ResourceFileItem[]>([])
const resourceLoading = ref(false)
const resourceError = ref('')
const resourceFilePaneRef = ref<HTMLElement | null>(null)
const localFileInputRef = ref<HTMLInputElement | null>(null)
const resourceFilePaneWidth = ref(0)
const { isMobileWorkspace } = useLayoutViewport()
const pressedPreviewFilePath = ref('')
const playingAudioPath = ref('')
const droppingLocalFiles = ref(false)
const failedThumbFilePaths = ref<Record<string, true>>({})

const folderCtxShow = ref(false)
const folderCtxX = ref(0)
const folderCtxY = ref(0)
const folderCtxTarget = ref<ResourceFolderTreeNode | null>(null)
const fileCtxShow = ref(false)
const fileCtxX = ref(0)
const fileCtxY = ref(0)
const fileCtxTarget = ref<ResourceFileItem | null>(null)

const addSubfolderOpen = ref(false)
const addSubfolderParent = ref<DataFileMap | null>(null)
const addSubfolderParentPath = ref('')
const renameResourceOpen = ref(false)
const renameResourceInitialName = ref('')
const renameResourceTarget = ref<ResourceFileItem | null>(null)
const selectedResourceFilePath = ref('')
const resourceClipboard = ref<ResourceClipboard | null>(null)

let resourcePreviewUrls: string[] = []
const generatedFontPreviewUrls = new Map<string, string>()
let resourceCategoryLoadToken = 0
let resourceFileLoadToken = 0
let resourceFilePaneResizeObserver: ResizeObserver | null = null
let windowResourceDragGuard: ((event: DragEvent) => void) | null = null
let lastTouchContextAt = 0

/* ——— 计算属性 ——— */

/** 供 n-tree 使用的数据（隐藏分类根，只显示其子目录） */
const resourceFolderTreeData = computed<ResourceFolderTreeNode[]>(() =>
  resourceFolderTreeRoot.value?.children ?? [],
)

/** 当前选中的树节点 key 列表（path 与 ResourceFolderItem.path 一致） */
const selectedFolderTreeKeys = computed<Array<string | number>>(() => [
  selectedResourceFolderPath.value,
])

const isVirtualTreeActive = computed(
  () => workspace.virtualResourceRoots[selectedResourceCategory.value] != null,
)

const resourceGridCols = computed(() => {
  const paneWidth = Math.max(resourceFilePaneWidth.value, 280)
  const cardWidth = isMobileWorkspace.value ? TOUCH_CARD_WIDTH : DESKTOP_CARD_WIDTH
  return Math.max(2, Math.floor((paneWidth - 8) / cardWidth))
})

const resourceFileRowHeight = computed(() => (isMobileWorkspace.value ? 116 : 104))

const resourceGridVars = computed(
  () =>
    `--resource-grid-cols:${resourceGridCols.value};--resource-card-w:${
      isMobileWorkspace.value ? TOUCH_CARD_WIDTH : DESKTOP_CARD_WIDTH
    }px;--resource-card-min-h:${
      isMobileWorkspace.value ? 104 : 96
    }px;--resource-thumb-h:${isMobileWorkspace.value ? 58 : 52}px;`,
)

const previewResourceFile = computed(
  () => resourceFiles.value.find((item) => item.path === pressedPreviewFilePath.value) ?? null,
)

const selectedResourceFile = computed(
  () => resourceFiles.value.find((item) => item.path === selectedResourceFilePath.value) ?? null,
)

const folderCtxOptions = computed<DropdownOption[]>(() => {
  const t = folderCtxTarget.value
  const path = String(t?.key ?? '')
  const canAdd = !!(t && t.source === 'virtual' && canCreateSubfolderInPath(selectedResourceCategory.value, path))
  const canDel =
    !!(t && t.source === 'virtual' && path !== '')
    && !isLockedTopLevelFolderPath(selectedResourceCategory.value, path)
  const canPaste =
    !!(t && t.source === 'virtual' && resourceClipboard.value?.categoryKey === selectedResourceCategory.value)
    && canCreateSubfolderInPath(selectedResourceCategory.value, path)
  return [
    { label: '添加子文件夹', key: 'add', disabled: !canAdd },
    { label: '剪切', key: 'cut', disabled: !canDel },
    { label: '粘贴', key: 'paste', disabled: !canPaste },
    { label: '删除文件夹', key: 'del', disabled: !canDel },
  ]
})

const fileCtxOptions = computed<DropdownOption[]>(() => {
  const canDel =
    !!selectedResourceFolder.value &&
    selectedResourceFolder.value.source === 'virtual' &&
    fileCtxTarget.value?.virtualChildIndex !== undefined
  const canAddEvent = selectedResourceCategory.value === 'graphic' || selectedResourceCategory.value === 'audio'
  const canRename = canDel && selectedResourceCategory.value !== 'font'
  const options: DropdownOption[] = []
  options.push({
    label: '添加事件',
    key: 'add-event',
    disabled: !canAddEvent || !fileCtxTarget.value,
  })
  options.push({
    label: '重命名资源',
    key: 'rename',
    disabled: !canRename,
  })
  options.push({
    label: '剪切资源',
    key: 'cut',
    disabled: !canDel,
  })
  options.push({
    label: '删除资源',
    key: 'del',
    disabled: !canDel,
  })
  return options
})

/** 在子树中按 path（key）查找节点 */
function findFolderNodeByPath(
  node: ResourceFolderTreeNode,
  path: string,
): ResourceFolderTreeNode | null {
  if (node.key === path) return node
  if (!node.children?.length) return null
  for (const c of node.children) {
    const r = findFolderNodeByPath(c, path)
    if (r) return r
  }
  return null
}

const selectedResourceFolder = computed((): ResourceFolderItem | null => {
  const root = resourceFolderTreeRoot.value
  if (!root) return null
  const path = selectedResourceFolderPath.value
  const node = findFolderNodeByPath(root, path)
  if (!node) return null
  return { name: String(node.label), path: node.key, source: 'virtual', mapNode: node.mapNode }
})

const canAddLocalResources = computed(() => {
  const folder = selectedResourceFolder.value
  return !!(folder && folder.source === 'virtual' && canCreateSubfolderInPath(selectedResourceCategory.value, folder.path))
})

const localFileAccept = computed(() => {
  switch (selectedResourceCategory.value) {
    case 'graphic':
      return '.png,.jpg,.jpeg,.webp,.bmp,.svg,.oui'
    case 'audio':
      return 'audio/*,.mp3,.wav,.ogg,.aac,.m4a,.flac'
    case 'video':
      return isMobileWorkspace.value
        ? 'video/*'
        : 'video/*,.mp4,.webm,.ogg,.mov,.m4v'
    case 'font':
      return '.ttf,.otf,.woff,.woff2'
    default:
      return ''
  }
})

/** 按当前面板宽度和交互模式，将文件列表分成适合桌面 / iPad 的虚拟行。 */
const resourceFileVirtualRows = computed((): ResourceFileVirtualRow[] => {
  const cards: ResourceGridCard[] = []
  if (canAddLocalResources.value) {
    cards.push({ key: 'local-add-card', kind: 'add' })
  }
  cards.push(
    ...resourceFiles.value.map((file) => ({
      key: file.virtualChildIndex !== undefined ? `vf-${file.virtualChildIndex}-${file.name}` : file.path,
      kind: 'file' as const,
      file,
    })),
  )
  const rows: ResourceFileVirtualRow[] = []
  const cols = resourceGridCols.value
  for (let i = 0; i < cards.length; i += cols) {
    const slice = cards.slice(i, i + cols)
    rows.push({
      key: `row-${i}-${slice[0]?.key ?? i}`,
      cards: slice,
    })
  }
  return rows
})

/* ——— 工具方法 ——— */

function clearResourcePreviewUrls() {
  for (const url of resourcePreviewUrls) URL.revokeObjectURL(url)
  resourcePreviewUrls = []
  generatedFontPreviewUrls.clear()
}

function syncResourceFilePaneMetrics() {
  const el = resourceFilePaneRef.value
  if (!el) return
  resourceFilePaneWidth.value = el.clientWidth
}

function resourceFileBadge(file: ResourceFileItem) {
  return file.ext ? file.ext.toUpperCase() : 'FILE'
}

function showLocalImportProgress(totalFiles: number) {
  projectLoading.begin(totalFiles > 1 ? `加载 1/${totalFiles} 资源` : '加载资源', 'overlay')
}

function updateLocalImportProgress(
  fileIndex: number,
  totalFiles: number,
  progress: LocalResourceApplyProgress,
  fileName: string,
) {
  const safePercent = Math.max(0, Math.min(100, Number(progress.percent) || 0))
  const overallPercent = totalFiles > 0
    ? ((fileIndex + safePercent / 100) / totalFiles) * 100
    : safePercent
  projectLoading.set(
    overallPercent,
    totalFiles > 1
      ? `加载 ${fileName} 资源 (${fileIndex + 1}/${totalFiles})`
      : `加载 ${fileName} 资源`,
  )
}

function updateBatchLocalImportProgress(
  totalFiles: number,
  progress: LocalResourceApplyProgress,
) {
  const safePercent = Math.max(0, Math.min(100, Number(progress.percent) || 0))
  const currentIndex = Math.min(totalFiles, Math.max(1, Math.ceil((safePercent / 100) * totalFiles)))
  const status = totalFiles > 1
    ? `${progress.detail}，加载 ${currentIndex}/${totalFiles} 个资源`
    : progress.detail
  projectLoading.set(safePercent, status)
}

function finishLocalImportProgress() {
  projectLoading.complete()
}

function resetLocalImportProgress() {
  projectLoading.fail()
}

function clearResourceSelection() {
  selectedResourceFilePath.value = ''
}

function setSingleResourceSelection(file: ResourceFileItem) {
  selectedResourceFilePath.value = file.path
}

function isResourceFileSelected(file: ResourceFileItem): boolean {
  return selectedResourceFilePath.value === file.path
}

function cleanupRemovedResourceFileState(file: ResourceFileItem) {
  if (pressedPreviewFilePath.value === file.path) {
    pressedPreviewFilePath.value = ''
  }
  if (playingAudioPath.value === file.path && previewAudio) {
    stopHtmlAudio(previewAudio)
    playingAudioPath.value = ''
  }
  if (file.resourceUrl) {
    workspace.removeResourcePreviewOverride(file.resourceUrl)
  }
}

function clearWorkspaceCanvasPreview() {
  workspace.clearResourceCanvasPreview()
}

function clearPressedPreview(file?: ResourceFileItem) {
  if (file && pressedPreviewFilePath.value !== file.path) return
  pressedPreviewFilePath.value = ''
  clearWorkspaceCanvasPreview()
}

function setWorkspaceCanvasPreview(
  kind: 'image' | 'video' | 'oaf',
  url: string,
  name: string,
  resourceUrl: string,
  isSkinResource: boolean,
) {
  workspace.setResourceCanvasPreview(kind, url, name, resourceUrl, isSkinResource)
}

async function createGeneratedFontPreviewUrl(file: ResourceFileItem): Promise<string> {
  const cached = generatedFontPreviewUrls.get(file.resourceUrl)
  if (cached) return cached
  if (typeof FontFace === 'undefined') return ''
  const isDefaultFont = isDefaultProjectFontName(file.name) || (!!file.displayName && isDefaultProjectFontName(file.displayName))
  let displayName = isDefaultFont ? DEFAULT_PROJECT_FONT_DISPLAY_NAME : file.displayName || file.name.replace(/\.[^.]+$/i, '') || file.name
  let family = 'Microsoft YaHei, sans-serif'
  if (!isDefaultFont) {
    const fontBlob = await fetchProjectFontBlobFromUrl(file.resourceUrl)
    const parsedDisplayName = await resolveFontDisplayNameFromAsset(file.name, fontBlob)
    displayName = parsedDisplayName || displayName
    family = `avgmaker-resource-font-${Date.now()}-${Math.random().toString(16).slice(2)}`
    const fontUrl = URL.createObjectURL(fontBlob)
    try {
      const fontFace = new FontFace(family, `url(${fontUrl})`)
      await fontFace.load()
      document.fonts.add(fontFace)
      await document.fonts.load(`24px "${family}"`)
    } finally {
      URL.revokeObjectURL(fontUrl)
    }
  }
  enrichCurrentFontResourceMetadata(file, displayName)

  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 512
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#111827'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `42px "${family}"`
  ctx.fillText(displayName, canvas.width / 2, 142)
  ctx.font = `36px "${family}"`
  ctx.fillText('橙光字体预览', canvas.width / 2, 244)
  ctx.font = `30px "${family}"`
  ctx.fillText('春风十里 ABC 123', canvas.width / 2, 338)
  const previewBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png')
  })
  if (!previewBlob) return ''
  const previewUrl = URL.createObjectURL(previewBlob)
  resourcePreviewUrls.push(previewUrl)
  generatedFontPreviewUrls.set(file.resourceUrl, previewUrl)
  return previewUrl
}

function persistVirtualResourceMetadataSoon() {
  workspace.virtualResourcesDirty = true
  void workspace.persistVirtualResources({
    uploadToCloud: true,
    ignoreUploadError: true,
  })
}

function enrichCurrentFontResourceMetadata(file: ResourceFileItem, displayName: string) {
  if (selectedResourceCategory.value !== 'font') return
  if (file.virtualChildIndex === undefined) return
  const folder = selectedResourceFolder.value
  if (!folder || folder.source !== 'virtual') return
  const node = folder.mapNode.children[file.virtualChildIndex]
  if (!node || node.isFolder) return
  const cleanDisplayName = displayName.trim()
  if (!cleanDisplayName || node.links[FONT_DISPLAY_NAME_LINK_INDEX] === cleanDisplayName) return
  node.links[FONT_DISPLAY_NAME_LINK_INDEX] = cleanDisplayName
  const nextFiles = resourceFiles.value.map((item) =>
    item.path === file.path ? { ...item, displayName: cleanDisplayName } : item,
  )
  resourceFiles.value = nextFiles
  persistVirtualResourceMetadataSoon()
}

function showPressedPreview(file: ResourceFileItem) {
  if (!canPreviewByOverlay(file)) return
  pressedPreviewFilePath.value = file.path
  void applyCanvasPreview(file)
}

function extFromNameOrUrl(name: string, url: string): string {
  if (name.includes('.')) return name.split('.').pop()!.toLowerCase()
  try {
    const pathname = new URL(url, 'https://placeholder.local').pathname
    const seg = pathname.split('/').pop() ?? ''
    if (seg.includes('.')) return seg.split('.').pop()!.toLowerCase().split('?')[0] ?? ''
  } catch {
    /* ignore */
  }
  return ''
}

function thumbUrlForVirtualFile(category: ResourceCategoryKey, name: string, url: string): string | null {
  const previewOverride = workspace.resourcePreviewOverrides[url]
  if (previewOverride) return previewOverride
  const ext = extFromNameOrUrl(name, url)
  if (category === 'font' && ext && IMAGE_EXTENSIONS.has(ext)) {
    if (isBitmapFontVirtualUrl(url)) {
      return resolveBitmapFontPreviewUrl(workspace.project as MainProject | null, url) || null
    }
    return url
  }
  if (isBitmapFontVirtualUrl(url)) {
    return resolveBitmapFontPreviewUrl(workspace.project as MainProject | null, url) || null
  }
  if (category === 'video' && /^https?:\/\//i.test(url)) return url
  if (category === 'font') {
    return ext && IMAGE_EXTENSIONS.has(ext) ? url : null
  }
  if (category !== 'graphic') return null
  if (isOafResource(name) || isOafResource(url)) return null
  if (ext && IMAGE_EXTENSIONS.has(ext)) return url
  if (/^https?:\/\//i.test(url)) return url
  return null
}

function isFontResourceFile(file: ResourceFileItem): boolean {
  return selectedResourceCategory.value === 'font' && FONT_FILE_EXTENSIONS.has(file.ext.toLowerCase())
}

function isBitmapFontImageFile(file: ResourceFileItem): boolean {
  return selectedResourceCategory.value === 'font' && IMAGE_EXTENSIONS.has(file.ext.toLowerCase())
}

function isOafResourceFile(file: ResourceFileItem): boolean {
  return selectedResourceCategory.value === 'graphic'
    && (isOafResource(file.name) || isOafResource(file.resourceUrl))
}

function getFontPreviewUrl(node: DataFileMap, ext: string): string {
  if (!FONT_FILE_EXTENSIONS.has(ext.toLowerCase())) return ''
  const previewMd5 = node.links[FONT_PREVIEW_MD5_LINK_INDEX]?.trim().toLowerCase() ?? ''
  return previewMd5 ? buildCurrentProjectResourceUrl(previewMd5) : ''
}

function resourceFileDisplayName(file: ResourceFileItem): string {
  return isFontResourceFile(file) ? file.displayName : file.name
}

function hasUsableThumb(file: ResourceFileItem): boolean {
  return !!file.thumbUrl && !failedThumbFilePaths.value[file.path]
}

function onResourceThumbError(file: ResourceFileItem) {
  failedThumbFilePaths.value = {
    ...failedThumbFilePaths.value,
    [file.path]: true,
  }
}

function canPreviewByOverlay(file: ResourceFileItem): boolean {
  const ext = file.ext.toLowerCase()
  if (selectedResourceCategory.value === 'graphic') {
    return !!file.thumbUrl || IMAGE_EXTENSIONS.has(ext) || isOafResourceFile(file)
  }
  if (selectedResourceCategory.value === 'video') {
    return !!file.resourceUrl || VIDEO_EXTENSIONS.has(ext)
  }
  if (selectedResourceCategory.value === 'font') {
    if (FONT_FILE_EXTENSIONS.has(ext)) return true
    return getTopFolderRuleByPath('font', selectedResourceFolderPath.value)?.name === '位图'
      && (!!file.thumbUrl || IMAGE_EXTENSIONS.has(ext))
  }
  return false
}

async function applyCanvasPreview(file: ResourceFileItem | null) {
  if (!file || !canPreviewByOverlay(file)) {
    clearWorkspaceCanvasPreview()
    return
  }
  if (isFontResourceFile(file)) {
    const previewUrl = file.previewUrl || await createGeneratedFontPreviewUrl(file).catch(() => {
      return ''
    })
    if (pressedPreviewFilePath.value !== file.path) return
    if (!previewUrl) {
      clearWorkspaceCanvasPreview()
      return
    }
    setWorkspaceCanvasPreview('image', previewUrl, file.displayName || file.name, file.resourceUrl, false)
    return
  }
  const previewUrl = file.previewUrl ?? file.resourceUrl ?? file.thumbUrl ?? ''
  if (!previewUrl) {
    clearWorkspaceCanvasPreview()
    return
  }
  const kind = isOafResourceFile(file) ? 'oaf' : selectedResourceCategory.value === 'video' ? 'video' : 'image'
  setWorkspaceCanvasPreview(kind, previewUrl, file.name, file.resourceUrl, file.isSkinResource)
}

function isAudioFile(file: ResourceFileItem): boolean {
  return selectedResourceCategory.value === 'audio' || AUDIO_EXTENSIONS.has(file.ext.toLowerCase())
}

function toggleAudioPreview(file: ResourceFileItem) {
  const src = file.previewUrl ?? file.resourceUrl ?? file.thumbUrl ?? ''
  if (!previewAudio || !src) return
  if (playingAudioPath.value === file.path) {
    stopHtmlAudio(previewAudio)
    playingAudioPath.value = ''
    return
  }
  playingAudioPath.value = file.path
  void playHtmlAudio(previewAudio, src, {
    mimeHint: inferAudioMimeFromName(file.name),
    onEnded: () => {
      playingAudioPath.value = ''
    },
  }).catch(() => {
    playingAudioPath.value = ''
  })
}

function dragHasVirtualMove(e: DragEvent): boolean {
  return virtualPaneHasFileMove(e)
}

function dragHasExternalFiles(e: DragEvent): boolean {
  const dt = e.dataTransfer
  if (!dt) return false
  if ((dt.files?.length ?? 0) > 0) return true
  if ((dt.items?.length ?? 0) > 0) {
    return Array.from(dt.items).some((item) => item.kind === 'file')
  }
  return Array.from(dt.types ?? []).includes('Files')
}

function shouldGuardExternalFileNavigation(event: DragEvent): boolean {
  return dragHasExternalFiles(event)
}

function shouldGuardInternalResourceNavigation(): boolean {
  return workspace.canvasDragResource?.source === 'native'
}

function getDroppedSupportedFiles(e: DragEvent): File[] {
  const dt = e.dataTransfer
  if (!dt) return []
  return Array.from(dt.files).filter((file) => canApplyLocalResource(selectedResourceCategory.value, file.name))
}

function isOuiFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.oui')
}

function openLocalFilePicker() {
  if (!canAddLocalResources.value || droppingLocalFiles.value) return
  const input = localFileInputRef.value
  if (!input) return
  input.value = ''
  input.click()
}

function onLocalFileInputChange(event: Event) {
  const input = event.target as HTMLInputElement | null
  const folder = selectedResourceFolder.value
  const files = Array.from(input?.files ?? [])
  if (input) input.value = ''
  if (!files.length) return
  if (!folder || folder.source !== 'virtual') {
    dialog.warning({
      title: '无法加载本地资源',
      content: '请先在左侧选择一个可用的资源文件夹',
    })
    return
  }
  const supportedFiles = files.filter((file) => canApplyLocalResource(selectedResourceCategory.value, file.name))
  if (!supportedFiles.length) {
    dialog.warning({
      title: '没有可导入的文件',
      content: '当前选择的文件类型与资源分类不匹配',
    })
    return
  }
  void uploadDroppedFilesToVirtualFolder(folder.path, supportedFiles).catch((error) => {
    dialog.error({
      title: '资源上传失败',
      content: error instanceof Error ? error.message : '本地文件上传失败',
    })
  })
}

function ensureVirtualRootForCurrentCategory(): DataFileMap {
  const existing = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (existing) {
    syncVirtualResourcePathMetadata(selectedResourceCategory.value, existing)
    return existing
  }
  const root = buildDefaultVirtualResourceRoot(selectedResourceCategory.value)
  workspace.setVirtualResourceRoot(selectedResourceCategory.value, root)
  return root
}

async function uploadDroppedFilesToVirtualFolder(folderPath: string, files: File[]) {
  const project = workspace.project as MainProject | null
  if (!project) {
    throw new Error('当前没有已打开工程')
  }

  if (!files.length) return
  if (!canCreateSubfolderInPath(selectedResourceCategory.value, folderPath)) {
    throw new Error('请先选择固定资源目录或其子目录，再执行上传')
  }
  droppingLocalFiles.value = true
  resourceError.value = ''
  showLocalImportProgress(files.length)
  let importSucceeded = false
  try {
    const ouiFiles = files.filter((file) => isOuiFile(file))
    if (ouiFiles.length) {
      if (selectedResourceCategory.value !== 'graphic') {
        throw new Error('.oui 皮肤文件只能拖入图像资源分类')
      }
      if (files.length !== 1 || ouiFiles.length !== 1) {
        throw new Error('一次只能拖入一个 .oui 皮肤文件')
      }
      if (!auth.loggedIn || !auth.sessionId) {
        throw new Error('请先登录后再导入 .oui 皮肤')
      }
      const applied = await applyLocalOuiSkinResource({
        file: ouiFiles[0]!,
        project,
        sessionId: auth.sessionId,
        ossUid: auth.uid,
        roots: workspace.virtualResourceRoots,
        dataDirectoryHandle: workspace.dataDirectoryHandle,
        onProgress: (progress) => {
          updateLocalImportProgress(0, 1, progress, ouiFiles[0]!.name)
        },
      })
      workspace.replaceVirtualResourceRoots(applied.roots)
      await workspace.uploadVirtualResourcesToCloud()
      workspace.revealResourceBrowserFolder('graphic', ['UI'])
      importSucceeded = true
      finishLocalImportProgress()
      return
    }

    if (selectedResourceCategory.value === 'font') {
      if (!auth.loggedIn || !auth.sessionId) {
        throw new Error('请先登录后再上传字体资源')
      }
      let currentRoot = ensureVirtualRootForCurrentCategory()
      for (const [index, file] of files.entries()) {
        const result = await applyLocalFontResource({
          file,
          project,
          sessionId: auth.sessionId,
          ossUid: auth.uid,
          folderPath,
          currentRoot,
          allRoots: workspace.virtualResourceRoots,
          dataDirectoryHandle: workspace.dataDirectoryHandle,
          onProgress: (progress) => {
            updateLocalImportProgress(index, files.length, progress, file.name)
          },
        })
        currentRoot = result.root
        workspace.setVirtualResourceRoot('font', currentRoot)
      }
      await workspace.uploadVirtualResourcesToCloud()
      workspace.revealResourceBrowserFolder('font', folderPath.split('/').filter(Boolean))
      importSucceeded = true
      finishLocalImportProgress()
      return
    }

    if (!auth.loggedIn || !auth.sessionId) {
      throw new Error('请先登录后再上传图像/音频/视频资源')
    }

    const currentRoot = ensureVirtualRootForCurrentCategory()
    const next = await applyLocalVirtualResources({
      category: selectedResourceCategory.value,
      folderPath,
      files,
      project,
      sessionId: auth.sessionId,
      ossUid: auth.uid,
      currentRoot,
      allRoots: workspace.virtualResourceRoots,
      dataDirectoryHandle: workspace.dataDirectoryHandle,
      onProgress: (progress) => {
        updateBatchLocalImportProgress(files.length, progress)
      },
    })
    workspace.setVirtualResourceRoot(selectedResourceCategory.value, next.root)
    for (const item of next.items) {
      workspace.setResourcePreviewOverride(item.url, URL.createObjectURL(item.file))
    }
    await workspace.uploadVirtualResourcesToCloud()
    workspace.revealResourceBrowserFolder(selectedResourceCategory.value, folderPath.split('/').filter(Boolean))
    importSucceeded = true
    finishLocalImportProgress()
  } finally {
    droppingLocalFiles.value = false
    if (!importSucceeded) {
      resetLocalImportProgress()
    }
  }
}

/**
 * 虚拟分类目录树（path：根为 ''，子文件夹为 a/b/c）
 */
function buildVirtualFolderBranch(map: DataFileMap, pathKey: string): ResourceFolderTreeNode {
  const folderChildren = map.children.filter((c) => c.isFolder)
  folderChildren.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'))
  const childNodes: ResourceFolderTreeNode[] = folderChildren.map((c) => {
    const childPath = pathKey ? `${pathKey}/${c.name}` : c.name
    return buildVirtualFolderBranch(c, childPath)
  })
  return {
    key: pathKey,
    label: map.name,
    source: 'virtual',
    mapNode: map,
    ...(childNodes.length ? { children: childNodes } : {}),
  }
}

function pathKeyForVirtualFolder(root: DataFileMap, target: DataFileMap): string {
  if (target === root) return ''
  function walk(node: DataFileMap, pathPrefix: string): string | null {
    for (const c of node.children) {
      if (!c.isFolder) continue
      const nextPath = pathPrefix ? `${pathPrefix}/${c.name}` : c.name
      if (c === target) return nextPath
      const sub = walk(c, nextPath)
      if (sub !== null) return sub
    }
    return null
  }
  const hit = walk(root, '')
  return hit ?? ''
}

function refreshVirtualTreeUi() {
  const cfg = RESOURCE_CATEGORIES.find((c) => c.key === selectedResourceCategory.value)
  const vr = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (!cfg || !vr) return
  syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
  const tree = buildVirtualCategoryTree(vr, cfg, selectedResourceCategory.value)
  resourceFolderTreeRoot.value = tree
  selectedResourceFolderPath.value = normalizeSelectedFolderPath(tree, selectedResourceFolderPath.value)
  void loadResourceFiles()
}

function openFolderContextAt(x: number, y: number, option: ResourceFolderTreeNode) {
  if (option.source !== 'virtual') return
  fileCtxShow.value = false
  folderCtxTarget.value = option
  folderCtxX.value = x
  folderCtxY.value = y
  folderCtxShow.value = true
}

function openFolderContextMenu(e: MouseEvent, option: ResourceFolderTreeNode) {
  if (option.source !== 'virtual') return
  e.preventDefault()
  e.stopPropagation()
  if (shouldBlockTouchContextMenu(e)) return
  openFolderContextAt(e.clientX, e.clientY, option)
}

function openFileContextMenu(
  e: MouseEvent | PointerEvent,
  file: ResourceFileItem,
  options?: { force?: boolean },
) {
  if (!selectedResourceFolder.value || selectedResourceFolder.value.source !== 'virtual') return
  if (file.virtualChildIndex === undefined) return
  e.preventDefault()
  e.stopPropagation()
  if (isMobileWorkspace.value && !options?.force) {
    return
  }
  if (!isResourceFileSelected(file)) {
    setSingleResourceSelection(file)
  }
  folderCtxShow.value = false
  fileCtxTarget.value = file
  fileCtxX.value = e.clientX
  fileCtxY.value = e.clientY
  fileCtxShow.value = true
}

function isTouchGeneratedContextMenu(event: MouseEvent | PointerEvent): boolean {
  const sourceCapabilities = (event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities
  if (sourceCapabilities) {
    return Boolean(sourceCapabilities.firesTouchEvents)
  }
  return performance.now() - lastTouchContextAt < 1200
}

/** 触屏设备统一拦截系统长按菜单（横竖屏均生效）。 */
function shouldBlockTouchContextMenu(event: MouseEvent | PointerEvent): boolean {
  if (isMobileWorkspace.value) return true
  return isTouchGeneratedContextMenu(event)
}

function rememberTouchContext(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') {
    lastTouchContextAt = performance.now()
  }
}

function openFolderMoreMenu(event: MouseEvent, option: ResourceFolderTreeNode) {
  event.preventDefault()
  event.stopPropagation()
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  openFolderContextAt(rect.right, rect.bottom, option)
}

function renderFolderTreeLabel(info: { option: TreeOption }) {
  const option = info.option as ResourceFolderTreeNode
  const isSelected = selectedFolderTreeKeys.value.includes(option.key)
  return h('span', { class: 'resource-folder-node-label' }, [
    h('span', { class: 'resource-folder-node-label__text' }, String(option.label ?? '')),
    option.source === 'virtual' && isSelected
      ? h(
        'span',
        {
          class: 'resource-folder-node-more',
          role: 'button',
          tabindex: 0,
          title: '更多操作',
          'aria-label': '更多操作',
          onClick: (event: MouseEvent) => openFolderMoreMenu(event, option),
          onMousedown: (event: MouseEvent) => event.stopPropagation(),
          onPointerdown: (event: PointerEvent) => event.stopPropagation(),
        },
        '更多',
      )
      : null,
  ])
}

function folderTreeNodeProps(info: { option: TreeOption }) {
  const o = info.option as ResourceFolderTreeNode
  if (!isVirtualTreeActive.value || o.source !== 'virtual') return {}
  return {
    onContextmenu: (e: MouseEvent) => openFolderContextMenu(e, o),
    onPointerdown: (e: PointerEvent) => rememberTouchContext(e),
    onDragover: (e: DragEvent) => {
      if (dragHasVirtualMove(e) || dragHasExternalFiles(e)) {
        e.preventDefault()
        if (e.dataTransfer) e.dataTransfer.dropEffect = dragHasExternalFiles(e) ? 'copy' : 'move'
      }
    },
    onDrop: (e: DragEvent) => onVirtualFileDropOnFolderNode(e, o),
  }
}

function virtualPaneHasFileMove(e: DragEvent): boolean {
  const dt = e.dataTransfer
  if (!dt) return false
  return Array.from(dt.types).includes(VIRTUAL_FILE_MOVE_MIME)
}

function onVirtualFolderDrop(info: {
  node: TreeOption
  dragNode: TreeOption
  dropPosition: 'before' | 'after' | 'inside'
}) {
  const vr = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (!vr) return
  const drag = info.dragNode as ResourceFolderTreeNode
  const drop = info.node as ResourceFolderTreeNode
  if (drag.source !== 'virtual' || drop.source !== 'virtual') return
  const dragPath = String(drag.key)
  const dropPath = String(drop.key)
  if (dragPath === '' || isLockedTopLevelFolderPath(selectedResourceCategory.value, dragPath)) return
  if (dropPath === '') return
  const dropParent = findParent(vr, drop.mapNode)
  if (info.dropPosition !== 'inside' && dropParent === vr) return
  applyVirtualFolderDrop(vr, drag.mapNode, drop.mapNode, info.dropPosition)
  syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
  refreshVirtualTreeUi()
}

function onFolderCtxSelect(key: string) {
  folderCtxShow.value = false
  const opt = folderCtxTarget.value
  const vr = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (!opt || opt.source !== 'virtual' || !vr) return
  if (key === 'add') {
    if (!canCreateSubfolderInPath(selectedResourceCategory.value, String(opt.key))) return
    addSubfolderParent.value = opt.mapNode
    addSubfolderParentPath.value = String(opt.key)
    addSubfolderOpen.value = true
    return
  }
  if (key === 'cut') {
    if (String(opt.key) === '' || isLockedTopLevelFolderPath(selectedResourceCategory.value, String(opt.key))) return
    resourceClipboard.value = {
      kind: 'folder',
      categoryKey: selectedResourceCategory.value,
      path: String(opt.key),
      node: opt.mapNode,
    }
    return
  }
  if (key === 'paste') {
    pasteResourceClipboard(opt)
    return
  }
  if (key === 'del') {
    if (String(opt.key) === '' || isLockedTopLevelFolderPath(selectedResourceCategory.value, String(opt.key))) return
    const delPath = String(opt.key)
    dialog.warning({
      title: '删除文件夹',
      content: '将删除该文件夹及其中的虚拟资源条目，是否继续？',
      positiveText: '删除',
      negativeText: '取消',
      onPositiveClick: () => {
        const parent = findParent(vr, opt.mapNode)
        removeNodeFromTree(vr, opt.mapNode)
        syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
        if (selectedResourceFolderPath.value === delPath) {
          selectedResourceFolderPath.value = parent ? pathKeyForVirtualFolder(vr, parent) : ''
        }
        refreshVirtualTreeUi()
        return true
      },
    })
  }
}

function pasteResourceClipboard(target: ResourceFolderTreeNode): void {
  const clipboard = resourceClipboard.value
  const vr = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (!clipboard || !vr || target.source !== 'virtual') return
  if (!canCreateSubfolderInPath(selectedResourceCategory.value, String(target.key))) return
  if (clipboard.kind === 'folder') {
    if (
      clipboard.path === String(target.key)
      || String(target.key).startsWith(`${clipboard.path}/`)
      || isLockedTopLevelFolderPath(selectedResourceCategory.value, clipboard.path)
    ) return
    if (!findParent(vr, clipboard.node) || !removeNodeFromTree(vr, clipboard.node)) return
    target.mapNode.children.push(clipboard.node)
    syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
    selectedResourceFolderPath.value = pathKeyForVirtualFolder(vr, target.mapNode)
    resourceClipboard.value = null
    refreshVirtualTreeUi()
    return
  }
  const sourceFolder = vr.findFolderByRelativePath(clipboard.folderPath)
  if (!sourceFolder) return
  moveVirtualFile(sourceFolder, clipboard.childIndex, target.mapNode)
  syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
  resourceClipboard.value = null
  notifySelectionActionCompleted()
  refreshVirtualTreeUi()
}

function deleteSelectedResourceFile() {
  const folder = selectedResourceFolder.value
  const file = selectedResourceFile.value
  if (!folder || folder.source !== 'virtual' || !file || file.virtualChildIndex === undefined) return
  dialog.warning({
    title: '删除资源',
    content: `将删除资源“${file.name}”，是否继续？`,
    positiveText: '删除',
    negativeText: '取消',
    onPositiveClick: () => {
      const childIndex = file.virtualChildIndex ?? -1
      if (childIndex < 0) return true
      const child = folder.mapNode.children[childIndex]
      if (!child || child.isFolder) return true
      folder.mapNode.children.splice(childIndex, 1)
      cleanupRemovedResourceFileState(file)
      clearWorkspaceCanvasPreview()
      notifySelectionActionCompleted()
      refreshVirtualTreeUi()
      return true
    },
  })
}

function normalizeResourceReferencePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')
}

function replaceEventResourceReferenceList(
  events: Array<{ Parameter?: string[] }>,
  oldPath: string,
  newPath: string,
): number {
  let count = 0
  for (const event of events) {
    if (!Array.isArray(event.Parameter)) continue
    event.Parameter = event.Parameter.map((item) => {
      if (normalizeResourceReferencePath(String(item)) !== oldPath) return item
      count += 1
      return newPath
    })
  }
  return count
}

/**
 * 同步工程内事件参数中的资源引用。
 * @param oldRelativePath 重命名前资源相对路径。
 * @param newRelativePath 重命名后资源相对路径。
 * @returns 被替换的参数数量。
 */
function syncResourceRenameEventReferences(
  oldRelativePath: string,
  newRelativePath: string,
): number {
  const project = workspace.project
  if (!project) return 0
  const oldPath = normalizeResourceReferencePath(oldRelativePath)
  const newPath = normalizeResourceReferencePath(newRelativePath)
  if (!oldPath || !newPath || oldPath === newPath) return 0
  let count = 0
  for (const map of project.storyTree.Maps ?? []) {
    count += replaceEventResourceReferenceList(map.List, oldPath, newPath)
  }
  const customUiList = project.systemData.customUI.CUIList ?? []
  for (const data of customUiList) {
    count += replaceEventResourceReferenceList(data.LoadEvent, oldPath, newPath)
    count += replaceEventResourceReferenceList(data.AfterEvent, oldPath, newPath)
    for (const item of data.ControlList ?? []) {
      count += replaceEventResourceReferenceList(item.EventList, oldPath, newPath)
    }
  }
  if (count > 0) {
    editor.touchSceneEvents()
  }
  return count
}

function splitResourceFileName(name: string): { baseName: string; extension: string } {
  const dotIndex = name.lastIndexOf('.')
  if (dotIndex <= 0) return { baseName: name, extension: '' }
  return {
    baseName: name.slice(0, dotIndex),
    extension: name.slice(dotIndex),
  }
}

function startRenameSelectedResourceFile() {
  const file = selectedResourceFile.value
  if (!file || file.virtualChildIndex === undefined || selectedResourceCategory.value === 'font') return
  renameResourceTarget.value = file
  renameResourceInitialName.value = splitResourceFileName(file.name).baseName
  renameResourceOpen.value = true
}

function confirmRenameResourceFile(value: string) {
  const folder = selectedResourceFolder.value
  const file = renameResourceTarget.value
  const vr = workspace.virtualResourceRoots[selectedResourceCategory.value]
  const nextBaseName = value.trim()
  if (!workspace.project || !folder || folder.source !== 'virtual' || !file || !vr) return false
  const childIndex = file.virtualChildIndex
  if (childIndex === undefined) return false
  if (selectedResourceCategory.value === 'font') return false
  if (!nextBaseName) {
    dialog.error({ title: '重命名资源', content: '资源名称不能为空' })
    return false
  }
  if (/[\\/]/.test(nextBaseName)) {
    dialog.error({ title: '重命名资源', content: '资源名称不能包含路径分隔符' })
    return false
  }
  const child = folder.mapNode.children[childIndex]
  if (!child || child.isFolder) return false
  const { extension } = splitResourceFileName(child.name)
  const nextName = `${nextBaseName}${extension}`
  if (child.name === nextName) return
  if (folder.mapNode.children.some((item) => item !== child && !item.isFolder && item.name === nextName)) {
    dialog.error({ title: '重命名资源', content: '同一文件夹下已存在同名资源' })
    return false
  }
  const oldRelativePath = child.relativePath || file.relativePath
  child.name = nextName
  syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
  const newRelativePath = child.relativePath
  const referenceCount = syncResourceRenameEventReferences(oldRelativePath, newRelativePath)
  selectedResourceFilePath.value = file.path.replace(/[^/\\]+$/, nextName)
  renameResourceTarget.value = null
  renameResourceInitialName.value = ''
  persistVirtualResourceMetadataSoon()
  refreshVirtualTreeUi()
  dialog.success({
    title: '重命名资源',
    content: referenceCount > 0 ? `已同步 ${referenceCount} 处事件引用` : '资源名称已更新',
  })
}

function cutSelectedResourceFile() {
  const folder = selectedResourceFolder.value
  const file = selectedResourceFile.value
  if (!folder || folder.source !== 'virtual' || !file || file.virtualChildIndex === undefined) return
  const node = folder.mapNode.children[file.virtualChildIndex]
  if (!node || node.isFolder) return
  resourceClipboard.value = {
    kind: 'file',
    categoryKey: selectedResourceCategory.value,
    folderPath: folder.path,
    childIndex: file.virtualChildIndex,
    node,
  }
}

function onFileCtxSelect(key: string) {
  fileCtxShow.value = false
  if (key === 'add-event') {
    if (fileCtxTarget.value) {
      startAddEventFromResource(fileCtxTarget.value)
    }
    return
  }
  if (key === 'rename') {
    startRenameSelectedResourceFile()
    return
  }
  if (key === 'cut') {
    cutSelectedResourceFile()
    return
  }
  if (key === 'del') {
    deleteSelectedResourceFile()
  }
}

function confirmAddSubfolder(value: string) {
  const parent = addSubfolderParent.value
  const vr = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (!parent || !vr) {
    addSubfolderOpen.value = false
    return
  }
  if (!canCreateSubfolderInPath(selectedResourceCategory.value, addSubfolderParentPath.value)) {
    addSubfolderOpen.value = false
    return
  }
  const name = uniqueFolderName(parent, value)
  parent.children.push(DataFileMap.categoryRoot(name))
  refreshVirtualTreeUi()
}

function initVirtualCategory() {
  workspace.setVirtualResourceRoot(selectedResourceCategory.value, buildDefaultVirtualResourceRoot(selectedResourceCategory.value))
  void loadResourceCategory()
}

function parseVirtualFileMovePayload(e: DragEvent): VirtualFileMovePayload | null {
  const raw = e.dataTransfer?.getData(VIRTUAL_FILE_MOVE_MIME)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<VirtualFileMovePayload>
    const childIndex = Number(parsed.childIndex)
    if (!parsed.categoryKey || !parsed.folderPath || !Number.isInteger(childIndex) || childIndex < 0) return null
    return {
      categoryKey: parsed.categoryKey as ResourceCategoryKey,
      folderPath: String(parsed.folderPath),
      childIndex,
    }
  } catch {
    return null
  }
}

function moveVirtualFile(
  sourceFolder: DataFileMap,
  childIndex: number,
  targetFolder: DataFileMap,
  insertBeforeIndex?: number,
): boolean {
  const node = sourceFolder.children[childIndex]
  if (!node || node.isFolder) return false
  let insertAt =
    insertBeforeIndex === undefined || insertBeforeIndex > targetFolder.children.length
      ? targetFolder.children.length
      : Math.max(0, insertBeforeIndex)
  if (sourceFolder === targetFolder) {
    insertAt -= childIndex < insertAt ? 1 : 0
  }
  sourceFolder.children.splice(childIndex, 1)
  targetFolder.children.splice(insertAt, 0, node)
  return true
}

function onVirtualFileDropOnFolderNode(e: DragEvent, target: ResourceFolderTreeNode) {
  e.preventDefault()
  e.stopPropagation()
  if (!canCreateSubfolderInPath(selectedResourceCategory.value, String(target.key))) return
  if (dragHasExternalFiles(e)) {
    const files = getDroppedSupportedFiles(e)
    if (!files.length) return
    startLocalImport(String(target.key), files)
    return
  }
  const payload = parseVirtualFileMovePayload(e)
  if (!payload) return
  if (target.source !== 'virtual') return
  if (!target.mapNode.isFolder) return
  applyVirtualResourceMove(payload, target.mapNode)
}

function onVirtualFilePaneDragOver(e: DragEvent) {
  if (!isVirtualTreeActive.value) return
  if (!dragHasVirtualMove(e) && !dragHasExternalFiles(e)) return
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = dragHasExternalFiles(e) ? 'copy' : 'move'
}

function onVirtualFilePaneDrop(e: DragEvent) {
  if (!isVirtualTreeActive.value) return
  e.preventDefault()
  e.stopPropagation()
  if (dragHasExternalFiles(e)) {
    const files = getDroppedSupportedFiles(e)
    if (!files.length) return
    const folder = selectedResourceFolder.value
    if (!folder || folder.source !== 'virtual') return
    startLocalImport(folder.path, files)
    return
  }
  const payload = parseVirtualFileMovePayload(e)
  if (!payload) return
  const folder = selectedResourceFolder.value
  if (!folder || folder.source !== 'virtual') return
  if (!canCreateSubfolderInPath(selectedResourceCategory.value, folder.path)) return
  applyVirtualResourceMove(payload, folder.mapNode)
}

function onVirtualResourceCardDragOver(e: DragEvent) {
  if (!dragHasVirtualMove(e) && !dragHasExternalFiles(e)) return
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = dragHasExternalFiles(e) ? 'copy' : 'move'
}

function onVirtualResourceCardDrop(e: DragEvent, targetFile: ResourceFileItem) {
  e.preventDefault()
  e.stopPropagation()
  if (dragHasExternalFiles(e)) {
    const files = getDroppedSupportedFiles(e)
    if (!files.length) return
    const folder = selectedResourceFolder.value
    if (!folder || folder.source !== 'virtual') return
    startLocalImport(folder.path, files)
    return
  }
  const payload = parseVirtualFileMovePayload(e)
  if (payload == null || targetFile.virtualChildIndex === undefined) return
  const folder = selectedResourceFolder.value
  if (!folder || folder.source !== 'virtual') return
  if (!canCreateSubfolderInPath(selectedResourceCategory.value, folder.path)) return
  if (payload.folderPath === folder.path && payload.childIndex === targetFile.virtualChildIndex) return
  const insertBefore = targetFile.virtualChildIndex
  applyVirtualResourceMove(payload, folder.mapNode, insertBefore)
}

function buildVirtualCategoryTree(
  root: DataFileMap,
  config: { folderName: string; label: string },
  category: ResourceCategoryKey,
): ResourceFolderTreeNode {
  const folderChildren = root.children.filter(
    (c) => c.isFolder && !isHiddenResourceTopFolder(category, c.name),
  )
  const childNodes: ResourceFolderTreeNode[] = folderChildren.map((c) =>
    buildVirtualFolderBranch(c, c.name),
  )
  const label = root.name?.trim() ? root.name : config.label
  return {
    key: '',
    label,
    source: 'virtual',
    mapNode: root,
    ...(childNodes.length ? { children: childNodes } : {}),
  }
}

function normalizeSelectedFolderPath(tree: ResourceFolderTreeNode, path: string): string {
  const [topSegment] = path.split('/').filter(Boolean)
  if (topSegment && isHiddenResourceTopFolder(selectedResourceCategory.value, topSegment)) {
    path = ''
  }
  if (path && findFolderNodeByPath(tree, path) !== null) return path
  if (selectedResourceCategory.value === 'font') {
    const fileFolder = tree.children?.find((child) => child.key === '文件')
    if (fileFolder) return String(fileFolder.key)
  }
  return tree.children?.[0]?.key ? String(tree.children[0].key) : ''
}

function buildExpandedFolderKeys(path: string): Array<string | number> {
  if (!path) return []
  const segments = path.split('/').filter(Boolean)
  const keys: string[] = []
  for (let i = 0; i < segments.length - 1; i += 1) {
    keys.push(segments.slice(0, i + 1).join('/'))
  }
  return keys
}

function onFolderTreeSelect(keys: Array<string | number>) {
  const k = keys[0]
  if (k === undefined || k === null) return
  selectedResourceFolderPath.value = String(k)
}

/* ——— 加载逻辑 ——— */

/** 切换资源分类时加载文件夹列表 */
async function loadResourceCategory() {
  const token = ++resourceCategoryLoadToken
  const config = RESOURCE_CATEGORIES.find((item) => item.key === selectedResourceCategory.value)
  clearWorkspaceCanvasPreview()
  clearResourcePreviewUrls()
  resourceError.value = ''
  resourceFiles.value = []
  resourceFolderTreeRoot.value = null
  expandedFolderKeys.value = []
  selectedResourceFolderPath.value = ''

  if (!config) {
    return
  }

  const virtualRoot = workspace.virtualResourceRoots[selectedResourceCategory.value]
  if (virtualRoot) {
    resourceLoading.value = true
    try {
      syncVirtualResourcePathMetadata(selectedResourceCategory.value, virtualRoot)
      const tree = buildVirtualCategoryTree(virtualRoot, config, selectedResourceCategory.value)
      if (token !== resourceCategoryLoadToken) return
      resourceFolderTreeRoot.value = tree
      selectedResourceFolderPath.value = normalizeSelectedFolderPath(tree, selectedResourceFolderPath.value)
      expandedFolderKeys.value = buildExpandedFolderKeys(selectedResourceFolderPath.value)
      resourceError.value = ''
    } finally {
      if (token === resourceCategoryLoadToken) resourceLoading.value = false
    }
    return
  }
  resourceFolderTreeRoot.value = null
  expandedFolderKeys.value = []
  selectedResourceFolderPath.value = ''
}

/** 切换文件夹时加载文件列表 */
async function loadResourceFiles() {
  const token = ++resourceFileLoadToken
  const folder = selectedResourceFolder.value
  if (previewAudio) stopHtmlAudio(previewAudio)
  playingAudioPath.value = ''
  clearWorkspaceCanvasPreview()
  clearResourcePreviewUrls()
  resourceFiles.value = []
  failedThumbFilePaths.value = {}
  pressedPreviewFilePath.value = ''
  if (!folder) return

  resourceLoading.value = true
  try {
    const files: ResourceFileItem[] = []
    const cat = selectedResourceCategory.value
    const onlyShowBitmapPng = cat === 'font' && getTopFolderRuleByPath('font', folder.path)?.name === '位图'
    for (let i = 0; i < folder.mapNode.children.length; i++) {
      const child = folder.mapNode.children[i]
      if (child.isFolder) continue
      const url = child.resourceUrl
      if (!url.trim()) continue
      const name = child.name || 'resource'
      const ext = extFromNameOrUrl(name, url)
      if (onlyShowBitmapPng && ext !== 'png') continue
      const pathMeta =
        child.relativePath && child.mappingPath
          ? { relativePath: child.relativePath, mappingPath: child.mappingPath }
          : resolveVirtualResourcePathMeta(cat, folder.path, name)
      let displayName = name
      if (cat === 'font' && FONT_FILE_EXTENSIONS.has(ext)) {
        const resolvedFontName = resolveFontDisplayNameFromStoredPath(
          workspace.virtualResourceRoots.font,
          pathMeta.relativePath || name,
        )
        displayName = resolvedFontName || name.replace(/\.[^.]+$/i, '') || name
      }
      const fontPreviewUrl = cat === 'font' ? getFontPreviewUrl(child, ext) : ''
      const isFontFile = cat === 'font' && FONT_FILE_EXTENSIONS.has(ext)
      const thumbUrl = isFontFile ? null : thumbUrlForVirtualFile(cat, name, url)
      const previewUrl = isFontFile
        ? (fontPreviewUrl || null)
        : (workspace.resourcePreviewOverrides[url]
          ?? (isBitmapFontVirtualUrl(url)
            ? resolveBitmapFontPreviewUrl(workspace.project as MainProject | null, url)
            : url))
      files.push({
        name,
        displayName,
        path: folder.path ? `${folder.path}/${name}` : name,
        resourceUrl: url,
        relativePath: pathMeta.relativePath,
        mappingPath: pathMeta.mappingPath,
        size: 0,
        ext,
        thumbUrl,
        previewUrl,
        dragPayload: pathMeta.relativePath,
        virtualChildIndex: i,
        isSkinResource: hasSkinResourceMarker(child),
      })
    }
    if (token !== resourceFileLoadToken) return
    resourceFiles.value = files
    resourceError.value = ''
  } catch (e: unknown) {
    if (token !== resourceFileLoadToken) return
    resourceError.value = e instanceof Error ? e.message : '资源读取失败'
  } finally {
    if (token === resourceFileLoadToken) resourceLoading.value = false
  }
}

/* ——— 拖拽（将资源路径传给事件编辑器） ——— */

function resourcePathForDrag(file: ResourceFileItem): string {
  return file.relativePath.trim()
}

function buildCanvasDragItem(file: ResourceFileItem) {
  const folderPath = selectedResourceFolder.value?.path ?? ''
  return {
    categoryKey: selectedResourceCategory.value,
    folderPath,
    relativePath: file.relativePath,
    mappingPath: file.mappingPath,
    resourceUrl: file.resourceUrl,
    name: file.name,
    ext: file.ext,
  }
}

function setDragData(dataTransfer: DataTransfer, type: string, value: string) {
  try {
    dataTransfer.setData(type, value)
  } catch {
    // 部分 Chromium 外壳会拒绝自定义 MIME，应用内拖拽会话仍可继续使用。
  }
}

function startAddEventFromResource(file: ResourceFileItem) {
  if (selectedResourceCategory.value !== 'graphic' && selectedResourceCategory.value !== 'audio') return
  if (!isResourceFileSelected(file)) {
    setSingleResourceSelection(file)
  }
  const dragItem = buildCanvasDragItem(file)
  workspace.setCanvasDragResource({
    categoryKey: selectedResourceCategory.value,
    folderPath: selectedResourceFolder.value?.path ?? '',
    relativePath: file.relativePath,
    mappingPath: file.mappingPath,
    resourceUrl: file.resourceUrl,
    name: file.name,
    ext: file.ext,
    source: 'touch',
  })
}

function populateNativeDragData(
  dataTransfer: DataTransfer | null | undefined,
  file: ResourceFileItem,
) {
  if (!dataTransfer) return
  const full = file.dragPayload ?? resourcePathForDrag(file)
  setDragData(dataTransfer, AVG_RESOURCE_PATH_MIME, full)
  setDragData(
    dataTransfer,
    AVG_CANVAS_RESOURCE_MIME,
    JSON.stringify({
      categoryKey: selectedResourceCategory.value,
      folderPath: selectedResourceFolder.value?.path ?? '',
      relativePath: file.relativePath,
      mappingPath: file.mappingPath,
      resourceUrl: file.resourceUrl,
      name: file.name,
      ext: file.ext,
    }),
  )
  const folder = selectedResourceFolder.value
  if (
    folder &&
    folder.source === 'virtual' &&
    file.virtualChildIndex !== undefined
  ) {
    const payload: VirtualFileMovePayload = {
      categoryKey: selectedResourceCategory.value,
      folderPath: folder.path,
      childIndex: file.virtualChildIndex,
    }
    setDragData(dataTransfer, VIRTUAL_FILE_MOVE_MIME, JSON.stringify(payload))
  }
  dataTransfer.effectAllowed = 'copyMove'
}

function clearWorkspaceCanvasDragResource() {
  workspace.clearCanvasDragResource()
}

function notifySelectionActionCompleted() {
  clearResourceSelection()
}

function reportLocalImportError(error: unknown) {
  dialog.error({
    title: '资源上传失败',
    content: error instanceof Error ? error.message : '本地文件上传失败',
  })
}

function startLocalImport(folderPath: string, files: File[]) {
  void uploadDroppedFilesToVirtualFolder(folderPath, files).catch((error) => {
    reportLocalImportError(error)
  })
}

function applyVirtualResourceMove(
  payload: VirtualFileMovePayload,
  targetFolder: DataFileMap,
  insertBeforeIndex?: number,
) {
  if (payload.categoryKey !== selectedResourceCategory.value) return false
  const vr = workspace.virtualResourceRoots[payload.categoryKey]
  if (!vr) return false
  const srcFolder = vr.findFolderByRelativePath(payload.folderPath)
  if (!srcFolder) return false
  if (!moveVirtualFile(srcFolder, payload.childIndex, targetFolder, insertBeforeIndex)) {
    return false
  }
  syncVirtualResourcePathMetadata(selectedResourceCategory.value, vr)
  notifySelectionActionCompleted()
  refreshVirtualTreeUi()
  return true
}

function handleDesktopResourceCardClick(event: MouseEvent, file: ResourceFileItem) {
  if (event.shiftKey || event.ctrlKey || event.metaKey) return
  setSingleResourceSelection(file)
  if (isFontResourceFile(file)) {
    pressedPreviewFilePath.value = file.path
    void applyCanvasPreview(file)
  }
}

function onResourceFileDragStart(e: DragEvent, file: ResourceFileItem) {
  setSingleResourceSelection(file)
  workspace.setCanvasDragResource({
    categoryKey: selectedResourceCategory.value,
    folderPath: selectedResourceFolder.value?.path ?? '',
    relativePath: file.relativePath,
    mappingPath: file.mappingPath,
    resourceUrl: file.resourceUrl,
    name: file.name,
    ext: file.ext,
    source: 'native',
  })
  populateNativeDragData(e.dataTransfer, file)
}

function onResourceFileDragEnd() {
  clearWorkspaceCanvasDragResource()
}

function onResourceFilePointerDown(event: PointerEvent, file: ResourceFileItem) {
  rememberTouchContext(event)
  setSingleResourceSelection(file)
  if (event.pointerType === 'touch') {
    event.preventDefault()
    showPressedPreview(file)
    return
  }
  showPressedPreview(file)
}

function onResourceFilePointerEnd(event: PointerEvent, file?: ResourceFileItem) {
  if (event.pointerType === 'touch') {
    event.stopPropagation()
    clearPressedPreview(file)
    return
  }
  if (file) {
    clearPressedPreview(file)
    return
  }
  clearPressedPreview()
}

function onResourceFilePointerCancel(event: PointerEvent, file?: ResourceFileItem) {
  if (event.pointerType === 'touch') {
    event.stopPropagation()
    clearPressedPreview(file)
    return
  }
  clearPressedPreview(file)
}

function onResourceFileCardClick(event: MouseEvent, file: ResourceFileItem) {
  if (isMobileWorkspace.value) {
    event.preventDefault()
    return
  }
  handleDesktopResourceCardClick(event, file)
}

function onResourceFileContextMenu(event: MouseEvent | PointerEvent, file: ResourceFileItem) {
  if (shouldBlockTouchContextMenu(event)) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  openFileContextMenu(event, file, { force: true })
}

function onResourcePaneContextMenu(event: MouseEvent) {
  if (!shouldBlockTouchContextMenu(event)) return
  event.preventDefault()
  event.stopPropagation()
}

function onResourceFileMoreClick(event: MouseEvent, file: ResourceFileItem) {
  event.preventDefault()
  event.stopPropagation()
  clearPressedPreview()
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  openFileContextMenu({
    clientX: rect.right,
    clientY: rect.bottom,
    preventDefault() {},
    stopPropagation() {},
  } as MouseEvent, file, { force: true })
}

/* ——— 侦听器 ——— */

watch(
  selectedResourceCategory,
  () => {
    resourceFolderTreeRoot.value = null
    expandedFolderKeys.value = []
    selectedResourceFolderPath.value = ''
    resourceFiles.value = []
    resourceError.value = ''
    clearResourceSelection()
  },
)

watch(
  [
    selectedResourceCategory,
    () => workspace.virtualResourceRoots[selectedResourceCategory.value],
  ],
  async () => {
    await loadResourceCategory()
  },
  { immediate: true },
)

watch(
  () => workspace.resourceBrowserReveal.token,
  async () => {
    const reveal = workspace.resourceBrowserReveal
    selectedResourceCategory.value = reveal.category
    await loadResourceCategory()
    selectedResourceFolderPath.value = reveal.folderPath
    expandedFolderKeys.value = buildExpandedFolderKeys(reveal.folderPath)
  },
)

watch(resourceFiles, (files) => {
  if (!files.length) {
    pressedPreviewFilePath.value = ''
    clearWorkspaceCanvasPreview()
    clearResourceSelection()
    return
  }
  const pressedStillExists = files.some((item) => item.path === pressedPreviewFilePath.value)
  if (!pressedStillExists) {
    pressedPreviewFilePath.value = ''
    clearWorkspaceCanvasPreview()
  }
  if (selectedResourceFilePath.value && !files.some((item) => item.path === selectedResourceFilePath.value)) {
    clearResourceSelection()
  }
})

watch(
  selectedResourceFolder,
  async () => { await loadResourceFiles() },
  { immediate: true },
)

onMounted(() => {
  previewAudio = new Audio()
  syncResourceFilePaneMetrics()
  if (typeof window !== 'undefined') {
    windowResourceDragGuard = (event: DragEvent) => {
      const isInternalResourceDrag = shouldGuardInternalResourceNavigation()
      if (!isInternalResourceDrag && !shouldGuardExternalFileNavigation(event)) return
      event.preventDefault()
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = isInternalResourceDrag ? 'none' : 'copy'
      }
      if (event.type === 'drop' && isInternalResourceDrag) {
        window.setTimeout(clearWorkspaceCanvasDragResource, 0)
      }
    }
    window.addEventListener('dragenter', windowResourceDragGuard, true)
    window.addEventListener('dragover', windowResourceDragGuard, true)
    window.addEventListener('drop', windowResourceDragGuard, true)
  }
  if (typeof ResizeObserver !== 'undefined' && resourceFilePaneRef.value) {
    resourceFilePaneResizeObserver = new ResizeObserver(() => {
      syncResourceFilePaneMetrics()
    })
    resourceFilePaneResizeObserver.observe(resourceFilePaneRef.value)
  }
})

onBeforeUnmount(() => {
  if (previewAudio) stopHtmlAudio(previewAudio)
  previewAudio = null
  resourceFilePaneResizeObserver?.disconnect()
  resourceFilePaneResizeObserver = null
  if (typeof window !== 'undefined' && windowResourceDragGuard) {
    window.removeEventListener('dragenter', windowResourceDragGuard, true)
    window.removeEventListener('dragover', windowResourceDragGuard, true)
    window.removeEventListener('drop', windowResourceDragGuard, true)
    windowResourceDragGuard = null
  }
  clearResourcePreviewUrls()
})
</script>

<template>
  <div class="resource-block">
    <!-- 分类标签页 -->
    <n-tabs
      v-model:value="selectedResourceCategory"
      type="segment"
      size="small"
      :animated="false"
      class="resource-tabs"
    >
      <n-tab-pane
        v-for="category in VISIBLE_RESOURCE_CATEGORIES"
        :key="category.key"
        :name="category.key"
        :tab="category.label"
      />
    </n-tabs>

    <div v-if="!isVirtualTreeActive" class="virtual-init-row">
      <button type="button" class="resource-action-button" @click="initVirtualCategory">启用虚拟资源（本分类）</button>
    </div>
    <n-dropdown
      trigger="manual"
      placement="bottom-start"
      :show="folderCtxShow"
      :x="folderCtxX"
      :y="folderCtxY"
      :options="folderCtxOptions"
      @select="onFolderCtxSelect"
      @clickoutside="folderCtxShow = false"
    />
    <n-dropdown
      trigger="manual"
      placement="bottom-start"
      :show="fileCtxShow"
      :x="fileCtxX"
      :y="fileCtxY"
      :options="fileCtxOptions"
      @select="onFileCtxSelect"
      @clickoutside="fileCtxShow = false"
    />
    <TextInputDialog
      v-model:show="addSubfolderOpen"
      title="添加子文件夹"
      model-value="新建文件夹"
      placeholder="文件夹名称"
      :confirm="confirmAddSubfolder"
    />
    <TextInputDialog
      v-model:show="renameResourceOpen"
      title="重命名资源"
      :model-value="renameResourceInitialName"
      placeholder="资源名称"
      :confirm="confirmRenameResourceFile"
    />
    <input
      ref="localFileInputRef"
      class="resource-local-file-input"
      type="file"
      :accept="localFileAccept"
      multiple
      @change="onLocalFileInputChange"
    />

    <!-- 左：文件夹树  右：文件网格 -->
    <div class="resource-browser">
      <div class="resource-folder-pane" @contextmenu="onResourcePaneContextMenu">
        <AppTreeView
          v-if="resourceFolderTreeData.length"
          :key="selectedResourceCategory"
          class="resource-folder-tree"
          :data="resourceFolderTreeData"
          :expanded-keys="expandedFolderKeys"
          :selected-keys="selectedFolderTreeKeys"
          :draggable="isVirtualTreeActive"
          :node-props="folderTreeNodeProps"
          :render-label="renderFolderTreeLabel"
          @update:expanded-keys="expandedFolderKeys = $event"
          @update:selected-keys="onFolderTreeSelect"
          @drop="onVirtualFolderDrop"
        />
      </div>

      <div
        ref="resourceFilePaneRef"
        class="resource-file-pane"
        @contextmenu="onResourcePaneContextMenu"
        @dragover="onVirtualFilePaneDragOver"
        @drop="onVirtualFilePaneDrop"
      >
          <div v-if="resourceLoading" class="resource-state">正在读取资源…</div>
          <div v-else-if="resourceError" class="resource-state resource-state-error">{{ resourceError }}</div>
          <div v-else-if="!workspace.virtualResourceRoots[selectedResourceCategory]" class="resource-state">当前分类尚未启用虚拟资源</div>
          <div v-else-if="!resourceFolderTreeRoot" class="resource-state">当前分类下暂无文件夹</div>
          <div v-else-if="!resourceFileVirtualRows.length" class="resource-state">当前文件夹暂无资源文件</div>
          <div v-else class="resource-file-virtual-wrap" :style="resourceGridVars">
            <n-virtual-list
              class="resource-file-virtual-list"
              :items="resourceFileVirtualRows"
              :item-size="resourceFileRowHeight"
              key-field="key"
            >
              <template #default="{ item }">
                <div class="resource-file-grid-row">
                  <div
                    v-for="card in item.cards"
                    :key="card.key"
                    class="resource-file-card"
                    :data-resource-file-path="card.kind === 'file' ? card.file.path : undefined"
                    :class="
                      card.kind === 'add'
                        ? {
                            'resource-file-card-add': true,
                            'resource-file-card-disabled': !canAddLocalResources || droppingLocalFiles,
                          }
                        : {
                            'resource-file-card-active': previewResourceFile?.path === card.file.path,
                            'resource-file-card-selected': isResourceFileSelected(card.file),
                            'resource-file-card-audio': isAudioFile(card.file),
                          }
                    "
                    :draggable="!isMobileWorkspace && card.kind === 'file'"
                    @pointerdown="card.kind === 'file' ? onResourceFilePointerDown($event, card.file) : undefined"
                    @pointerup="card.kind === 'file' ? onResourceFilePointerEnd($event, card.file) : undefined"
                    @pointercancel="card.kind === 'file' ? onResourceFilePointerCancel($event, card.file) : undefined"
                    @dragstart="!isMobileWorkspace && card.kind === 'file' ? onResourceFileDragStart($event, card.file) : undefined"
                    @dragend="!isMobileWorkspace && card.kind === 'file' ? onResourceFileDragEnd() : undefined"
                    @click="card.kind === 'add' ? openLocalFilePicker() : onResourceFileCardClick($event, card.file)"
                    @contextmenu="card.kind === 'file' ? onResourceFileContextMenu($event, card.file) : onResourcePaneContextMenu($event)"
                    @dragover="card.kind === 'file' ? onVirtualResourceCardDragOver($event) : undefined"
                    @drop="card.kind === 'file' ? onVirtualResourceCardDrop($event, card.file) : undefined"
                  >
                    <template v-if="card.kind === 'add'">
                      <div class="resource-file-thumb resource-file-thumb--add">
                        <span class="resource-file-add-icon">+</span>
                      </div>
                    </template>
                    <template v-else>
                    <div
                      class="resource-file-thumb"
                      :class="{ 'resource-file-thumb--bitmap-font': isBitmapFontImageFile(card.file) }"
                    >
                      <img
                        v-if="hasUsableThumb(card.file)"
                        :src="card.file.thumbUrl"
                        :alt="card.file.name"
                        class="resource-file-image"
                        draggable="false"
                        loading="lazy"
                        decoding="async"
                        @error="onResourceThumbError(card.file)"
                        @contextmenu.stop.prevent="onResourceFileContextMenu($event, card.file)"
                      />
                      <svg
                        v-else-if="isAudioFile(card.file)"
                        class="resource-audio-icon"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path d="M31 8v23.5a7.5 7.5 0 1 1-4-6.62V14.6l-12 2.78V36.5a7.5 7.5 0 1 1-4-6.62V15.8L31 11.1Z" />
                        <path d="M36.2 18.4c2.5 1.8 4 4.4 4 7.6s-1.5 5.8-4 7.6" />
                      </svg>
                      <svg
                        v-else-if="isFontResourceFile(card.file)"
                        class="resource-font-icon"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path d="M8 10h32v7h-3l-1.2-3H27v24h4v3H17v-3h4V14h-8.8L11 17H8Z" />
                        <path d="M31 25h9v3h-3v10h3v3h-9v-3h3V28h-3Z" />
                      </svg>
                      <svg
                        v-else-if="isOafResourceFile(card.file)"
                        class="resource-oaf-icon"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path d="M8 9h32v30H8Z" />
                        <path d="M14 15h20v18H14Z" />
                        <path d="M28 20v8l7-4Z" />
                      </svg>
                      <span v-else class="resource-file-badge">{{ resourceFileBadge(card.file) }}</span>
                    </div>
                    <div class="resource-file-name" :title="card.file.name">{{ resourceFileDisplayName(card.file) }}</div>
                    <span
                      class="resource-file-more"
                      role="button"
                      tabindex="0"
                      title="更多操作"
                      aria-label="更多操作"
                      @click="onResourceFileMoreClick($event, card.file)"
                      @pointerdown.stop
                    >
                      更多
                    </span>
                    <button
                      v-if="isAudioFile(card.file)"
                      type="button"
                      class="resource-audio-preview-button"
                      :class="{ 'resource-audio-preview-button-active': playingAudioPath === card.file.path }"
                      @click.stop="toggleAudioPreview(card.file)"
                    >
                      {{ playingAudioPath === card.file.path ? '停止' : '播放' }}
                    </button>
                    </template>
                  </div>
                </div>
              </template>
            </n-virtual-list>
          </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.resource-block {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 var(--pv-resource-min, 0px);
  gap: 0;
  background: var(--theme-bg-panel);
}

.virtual-init-row {
  flex-shrink: 0;
  padding: var(--app-space-xs) var(--app-space-sm);
}

.virtual-mode-hint {
  flex-shrink: 0;
  padding: 0 var(--app-space-sm) var(--app-space-xs);
  font-size: var(--app-font-size-xs);
  line-height: var(--app-line-height-normal);
  color: var(--theme-text-very-muted);
}

.resource-tabs {
  flex-shrink: 0;
}

.resource-action-button,
.resource-audio-preview-button {
  border: 1px solid var(--theme-border);
  box-sizing: border-box;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-card);
  font-size: var(--app-font-size-sm);
  line-height: var(--app-line-height-normal);
  cursor: pointer;
}

.resource-tabs :deep(.n-tabs-nav) {
  margin-bottom: 0 !important;
}

.resource-tabs :deep(.n-tabs-wrapper) {
  padding: 0 var(--app-space-sm);
}

.resource-tabs :deep(.n-tabs-tab) {
  padding-left: var(--app-space-md);
  padding-right: var(--app-space-md);
}

.resource-tabs :deep(.n-tabs-tab__label) {
  font-size: var(--app-font-size-sm);
}

.resource-action-button:hover,
.resource-audio-preview-button-active {
  border-color: var(--theme-accent);
  color: var(--theme-text-primary);
  background: var(--theme-bg-selected);
}

.resource-action-button {
  padding: 2px var(--app-space-sm);
}

.resource-block :deep(.n-input__input-el),
.resource-block :deep(.n-input__placeholder) {
  font-size: var(--app-font-size-sm);
}

.resource-browser {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(132px, 16%) minmax(0, 1fr);
  gap: 0;
  border-top: 1px solid var(--theme-border);
}

.resource-folder-pane,
.resource-file-pane {
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--theme-bg-card);
  border: 0;
  overflow: hidden;
  -webkit-touch-callout: none;
}

.resource-file-pane {
  border-left: 1px solid var(--theme-border);
  position: relative;
}

.resource-file-virtual-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: var(--app-space-sm);
}

.resource-file-virtual-list {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.resource-file-virtual-list :deep(.v-vl) {
  min-height: 0;
}

.resource-folder-tree :deep(.n-tree-node-content--selected) {
  border-left: 2px solid var(--theme-accent);
}

.resource-folder-tree :deep(.resource-folder-node-label) {
  display: inline-flex;
  align-items: center;
  gap: var(--app-space-xs);
  min-width: 0;
}

.resource-folder-tree :deep(.resource-folder-node-label__text) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resource-folder-tree :deep(.resource-folder-node-more) {
  all: unset;
  color: var(--theme-text-very-muted);
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.resource-folder-tree :deep(.resource-folder-node-more:hover),
.resource-folder-tree :deep(.resource-folder-node-more:focus-visible),
.resource-file-more:hover,
.resource-file-more:focus-visible {
  color: var(--theme-text-secondary);
}

.resource-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  padding: var(--app-space-lg);
  font-size: var(--app-font-size-xs);
  color: var(--theme-text-very-muted);
  background: var(--theme-bg-card);
}

.resource-state-error {
  color: var(--theme-error);
}

.resource-file-grid-row {
  display: grid;
  grid-template-columns: repeat(var(--resource-grid-cols, 4), var(--resource-card-w, 96px));
  gap: var(--app-space-sm);
  justify-content: start;
  padding: 0 0 var(--app-space-sm);
  box-sizing: border-box;
  min-height: 100%;
}

.resource-file-card {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-xs);
  width: var(--resource-card-w, 96px);
  min-height: var(--resource-card-min-h, 116px);
  padding: var(--app-space-xs) var(--app-space-xs) 18px;
  box-sizing: border-box;
  position: relative;
  background: var(--theme-bg-raised);
  border: 0;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
}

.resource-file-more {
  all: unset;
  position: absolute;
  right: var(--app-space-sm);
  bottom: 3px;
  color: var(--theme-text-very-muted);
  font-size: 10px;
  line-height: 12px;
  cursor: pointer;
  display: none;
}

.resource-local-file-input {
  display: none;
}

.resource-file-card:hover,
.resource-file-card-active {
  background: var(--theme-bg-selected);
}

.resource-file-card-selected {
  background: var(--theme-bg-selected);
  box-shadow: inset 0 0 0 2px var(--theme-accent);
}

.resource-file-card-add {
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: transparent;
}

.resource-file-card-add:hover {
  background: var(--theme-bg-selected);
}

.resource-file-card-disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.resource-file-card-audio {
  padding-bottom: 30px;
}

.resource-file-add-icon {
  font-size: 44px;
  line-height: 1;
  color: var(--theme-text-secondary);
}

.resource-file-card:active {
  cursor: grabbing;
}

@media (hover: hover) and (pointer: fine) {
  .resource-file-card-selected .resource-file-more,
  .resource-folder-tree :deep(.resource-folder-node-more:focus-visible),
  .resource-file-more:focus-visible {
    display: inline-block;
  }
}

.resource-file-card-selected .resource-file-more {
  display: inline-block;
}

.resource-file-thumb {
  height: var(--resource-thumb-h, 70px);
  max-height: var(--resource-thumb-h, 70px);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--theme-bg-panel);
  border: 0;
}

.resource-file-thumb--add {
  height: auto;
  max-height: none;
  padding: 0;
  background: transparent;
  border: 0;
}

.resource-file-thumb--bitmap-font {
  background: #111827;
}

.resource-file-image {
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.resource-file-badge {
  font-size: var(--app-font-size-xs);
  font-weight: 700;
  color: var(--theme-text-card);
}

.resource-audio-icon {
  width: 42px;
  height: 42px;
  color: var(--theme-accent);
  fill: currentColor;
}

.resource-audio-icon path:last-child {
  fill: none;
  stroke: currentColor;
  stroke-width: 4;
  stroke-linecap: round;
}

.resource-font-icon {
  width: 42px;
  height: 42px;
  color: var(--theme-accent);
  fill: currentColor;
}

.resource-oaf-icon {
  width: 42px;
  height: 42px;
  color: var(--theme-accent);
  fill: currentColor;
}

.resource-oaf-icon path:nth-child(2) {
  fill: var(--theme-bg-raised);
}

.resource-file-name {
  font-size: var(--app-font-size-sm);
  color: var(--theme-text-primary);
  height: 32px;
  overflow: hidden;
  line-height: 16px;
  word-break: break-all;
  text-align: center;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.resource-audio-preview-button {
  position: absolute;
  left: var(--app-space-xs);
  bottom: var(--app-space-xs);
  min-width: 42px;
  padding: 1px var(--app-space-xs);
}


@media (max-width: 1024px) {
  .resource-browser {
    grid-template-columns: minmax(120px, 20%) minmax(0, 1fr);
  }
}
</style>
